import { Loading } from 'module/ui/components/atoms/loading';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider, useIntl } from 'react-intl';
import styled from 'styled-components';
import translations from 'translations/en.json';

const SidebarLoadingFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: var(--spacing-06);
`;

const SidebarLoadingSpinner: React.FC = () => {
  const intl = useIntl();
  const message = intl.formatMessage({ id: 'document_agent.preview.loading' });

  return <Loading size="small" description={message} />;
};

const DocumentAgentDefineStepContent = React.lazy(() =>
  import('agentBuilder/DocumentAgentPreviewComponents').then((m) => ({
    default: m.DocumentAgentDefineStepContent,
  })),
);

const DocumentAgentTriggerStepContent = React.lazy(() =>
  import('agentBuilder/DocumentAgentPreviewComponents').then((m) => ({
    default: m.DocumentAgentTriggerStepContent,
  })),
);

const RemoteErrorFallback = ({ error }: { error: Error }) => (
  <div style={{ padding: 16 }}>
    <h2>Document agent preview failed to load</h2>
    <pre style={{ whiteSpace: 'pre-wrap' }}>{error.message}</pre>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: 1,
    },
  },
});

const QueryClientWrapper: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  try {
    useQueryClient();

    return <>{children}</>;
  } catch {
    return (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    );
  }
};

export type DocumentAgentHasAccessToResource = (args: {
  rule?: string;
}) => boolean;

const RemoteContentProviders: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <IntlProvider
    locale="en"
    messages={translations}
    fallbackOnEmptyString
    defaultLocale="en"
  >
    <QueryClientWrapper>{children}</QueryClientWrapper>
  </IntlProvider>
);

export type DocumentAgentDefineStepContentRemoteProps = {
  prompt: any;
  onPromptChange: (prompt: any) => void;
  isTemplateSelectDisabled?: boolean;
  readOnly?: boolean;
  isKnowledgeBaseEnabled?: boolean;
  hasAccessToResource?: DocumentAgentHasAccessToResource;
};

export const DocumentAgentDefineStepContentRemote: React.FC<
  DocumentAgentDefineStepContentRemoteProps
> = ({
  prompt,
  onPromptChange,
  isTemplateSelectDisabled,
  readOnly = false,
  isKnowledgeBaseEnabled = false,
  hasAccessToResource,
}) => (
  <RemoteContentProviders>
    <ErrorBoundary FallbackComponent={RemoteErrorFallback}>
      <React.Suspense
        fallback={
          <SidebarLoadingFallback>
            <SidebarLoadingSpinner />
          </SidebarLoadingFallback>
        }
      >
        <DocumentAgentDefineStepContent
          prompt={prompt}
          onPromptChange={onPromptChange}
          isTemplateSelectDisabled={isTemplateSelectDisabled || readOnly}
          readOnly={readOnly}
          isKnowledgeBaseEnabled={isKnowledgeBaseEnabled}
          hasAccessToResource={hasAccessToResource}
        />
      </React.Suspense>
    </ErrorBoundary>
  </RemoteContentProviders>
);

export type DocumentAgentTriggerStepContentRemoteProps = {
  prompt: any;
  onPromptChange: (prompt: any) => void;
  readOnly?: boolean;
};

export const DocumentAgentTriggerStepContentRemote: React.FC<
  DocumentAgentTriggerStepContentRemoteProps
> = ({ prompt, onPromptChange, readOnly = false }) => (
  <RemoteContentProviders>
    <ErrorBoundary FallbackComponent={RemoteErrorFallback}>
      <React.Suspense
        fallback={
          <SidebarLoadingFallback>
            <SidebarLoadingSpinner />
          </SidebarLoadingFallback>
        }
      >
        {/* readOnly is supported by agentBuilder DocumentAgentTriggerStepContent */}
        <DocumentAgentTriggerStepContent
          prompt={prompt}
          onPromptChange={onPromptChange}
          readOnly={readOnly}
        />
      </React.Suspense>
    </ErrorBoundary>
  </RemoteContentProviders>
);
